import type { RegistrationRequestDraft } from '../../../../core/identity/accountLink';

/**
 * Account-linking port — owned by this feature, implemented in `infrastructure/`.
 *
 * ## Why these are server operations, not Firestore reads
 *
 * An employee document is unreadable without a link, and that is correct: if an
 * unlinked account could query `employees` by email to find its own record, it
 * could enumerate the entire staff directory — names, phones, addresses — by
 * trying addresses. The rule that protects the directory is the same rule that
 * stops someone finding themselves in it.
 *
 * So the "is this you?" step cannot be a client read. It is a trusted lookup
 * that returns **one name and nothing else**, for the caller's own verified
 * email. Likewise, creating the link is the single most dangerous write in the
 * system (ADR-004) and has no client path at all.
 *
 * These three operations are therefore Cloud Functions, and this milestone
 * cannot ship without them.
 */

/** The result of asking "does the school have a record for my address?" */
export type IdentityLookup =
  | {
      readonly outcome: 'match';
      /**
       * Display name only. Deliberately nothing else — not the national ID, not
       * the phone, not the branch. Someone shown a name they can recognise can
       * confirm honestly; someone shown a name plus personal details has been
       * handed those details before proving anything (FR-004).
       */
      readonly employeeName: string;
      /** Opaque handle for the confirm step. Not the employee number. */
      readonly candidateToken: string;
    }
  | { readonly outcome: 'no_match' }
  | { readonly outcome: 'already_linked' }
  | { readonly outcome: 'awaiting_approval' };

export type LinkFailureCode =
  | 'wrong_domain'
  | 'already_linked'
  | 'record_taken'
  | 'candidate_expired'
  | 'network'
  | 'unknown';

export class AccountLinkError extends Error {
  readonly code: LinkFailureCode;

  constructor(code: LinkFailureCode, message: string) {
    super(message);
    this.name = 'AccountLinkError';
    this.code = code;
  }
}

export interface AccountLinkPort {
  /** Asks what the school knows about the caller's own verified address. */
  lookupMyIdentity(): Promise<IdentityLookup>;

  /**
   * Confirms "yes, that is me", creating the link.
   *
   * The token comes from the lookup rather than the client naming a record:
   * a client that could name the record it wants to be linked to could name
   * anyone's.
   */
  confirmIdentity(candidateToken: string): Promise<void>;

  /**
   * "That is not me."
   *
   * Separate from filing the request, and takes the same signed token the
   * confirm step does — the caller cannot name the record it is rejecting any
   * more than it can name the one it is claiming.
   *
   * Recorded server-side so the same name is not offered again, and so the
   * mismatched row is flagged for HR (FR-006).
   */
  declineIdentity(candidateToken: string): Promise<void>;

  /**
   * Files a self-reported registration for HR to review.
   *
   * A plain document write rather than a function: the security rules already
   * constrain it to the caller's own uid, their own verified email, and
   * `status: 'pending'`. A callable would add a hop and enforce nothing extra.
   */
  submitRegistrationRequest(draft: RegistrationRequestDraft): Promise<void>;
}
